import { doc, getDoc, updateDoc, setDoc } from "firebase/firestore";
import { db } from "../../../connection";
import logger from "../../../../../utils/logger";

export const updateUserCheckInPoints = async (
  groupId,
  clubId,
  memberId,
  eventId,
  season,
  memberName,
  memberRole,
  eventDetails,
  selectedLocationDetails,
  checkInPoints,
  initialCheckInPoints
) => {
  try {
    // Convert string inputs to numbers to ensure consistent handling
    const numCheckInPoints = Number(checkInPoints);
    const numInitialCheckInPoints = Number(initialCheckInPoints);

    // CASE 1: Update the totalEventPoints in the year doc
    const yearDocRef = doc(
      db,
      "leaderBoard",
      clubId,
      "groups",
      groupId,
      "eventLeaderBoard",
      season
    );

    // Get the current document to update totalEventPoints
    const yearDocSnap = await getDoc(yearDocRef);

    if (yearDocSnap.exists()) {
      const yearData = yearDocSnap.data();
      const memberData = yearData[memberId] || {};
      const currentTotal = Number(memberData.totalEventPoints || 0);

      // Calculate the new total by:
      // 1. Subtracting the initial check-in points
      // 2. Adding the new check-in points
      const newTotal =
        currentTotal - numInitialCheckInPoints + numCheckInPoints;

      // Create an update object with the nested field
      const updateObj = {};

      // If memberId doesn't exist in the yearDoc, add all fields
      if (!yearData[memberId]) {
        updateObj[memberId] = {
          totalEventPoints: newTotal,
          userName: memberName,
          isFanCheckIn: memberRole === "fan",
          timeStamp: Math.floor(new Date().getTime() / 1000),
          totalVolunteerPoints: 0,
        };
      } else {
        // Just update the totalEventPoints
        updateObj[`${memberId}.totalEventPoints`] = newTotal;
      }

      // Update the totalEventPoints
      await updateDoc(yearDocRef, updateObj);
    } else if (numCheckInPoints > 0) {
      // Create new entry if it doesn't exist and we have points to add
      const newData = {};
      newData[memberId] = {
        totalEventPoints: numCheckInPoints,
        userName: memberName,
        isFanCheckIn: memberRole === "fan",
        timeStamp: Math.floor(new Date().getTime() / 1000),
        totalVolunteerPoints: 0,
      };

      await setDoc(yearDocRef, newData);
    }

    // CASE 2: Update the eventSummary in users subcollection
    const userDocRef = doc(
      db,
      "leaderBoard",
      clubId,
      "groups",
      groupId,
      "eventLeaderBoard",
      season,
      "users",
      memberId
    );

    // Get the user document from the users subcollection
    const userDocSnap = await getDoc(userDocRef);

    if (userDocSnap.exists()) {
      const userData = userDocSnap.data();
      const eventSummary = userData.eventSummary || {};

      // Update the specific event's checkInPoint

      if (eventSummary[eventId]) {
        eventSummary[eventId] = {
          ...(eventSummary[eventId] || {}),
          checkInPoint: numCheckInPoints,
          isCheckedIn: numCheckInPoints > 0 ? true : false,
        };
      } else {
        eventSummary[eventId] = {
          checkInPoint: numCheckInPoints,
          isCheckedIn: numCheckInPoints > 0 ? true : false,
          userName: memberName || null,
          eventDetail: {
            address: selectedLocationDetails?.address || null,
            eventStartTime: eventDetails?.details?.eventStartDate || null,
            title: eventDetails?.title || null,
            type: eventDetails?.type || null,
          },
        };
      }

      // Update the eventSummary in the user document
      await updateDoc(userDocRef, { eventSummary });
    } else if (numCheckInPoints > 0) {
      // Create new user entry with the event summary only if we have points
      const newEventSummary = {
        [eventId]: {
          checkInPoint: numCheckInPoints,
          isCheckedIn: true,
          userName: memberName || null,
          eventDetail: {
            address: selectedLocationDetails?.address || null,
            eventStartTime: eventDetails?.details?.eventStartDate || null,
            title: eventDetails?.title || null,
            type: eventDetails?.type || null,
          },
        },
      };

      // Create the user document in the users subcollection
      await setDoc(userDocRef, { eventSummary: newEventSummary });
    }

    // CASE 3: Update the RSVP document to set checkedIn flag
    const rsvpDocRef = doc(
      db,
      "groupAdminPost",
      groupId,
      "posts",
      eventId,
      "rsvp",
      memberId
    );

    // Get the current RSVP document
    const rsvpDocSnap = await getDoc(rsvpDocRef);

    if (rsvpDocSnap.exists()) {
      // If check-in points > 0, set checkedIn to true
      if (numCheckInPoints > 0) {
        await updateDoc(rsvpDocRef, {
          checkedIn: true,
        });
      } else {
        // If check-in points = 0, set checkedIn to false
        await updateDoc(rsvpDocRef, {
          checkedIn: false,
        });
      }
    } else if (numCheckInPoints > 0) {
      // Create new RSVP document if it doesn't exist and we have check-in points
      await setDoc(rsvpDocRef, {
        name: memberName,
        checkedIn: true,
        rsvp: {
          status: "attending",
        },
        locationIndex: selectedLocationDetails?.locationIndex,
        volunteerDetails: {},
        volunteered: {},
      });
    }

    // CASE 4: Update the totalCheckIn in the location document
    if (selectedLocationDetails?.locationId) {
      const locationDocRef = doc(
        db,
        "group",
        groupId,
        "locations",
        selectedLocationDetails.locationId
      );

      const locationDocSnap = await getDoc(locationDocRef);

      if (locationDocSnap.exists()) {
        const locationData = locationDocSnap.data();
        const totalCheckIn = locationData.totalCheckIn || {};

        // Determine if we need to increment or decrement
        let checkInDifference = 0;

        // If check-in points were added when there were none before, increment by 1
        if (numInitialCheckInPoints === 0 && numCheckInPoints > 0) {
          checkInDifference = 1;
        }
        // If check-in points were removed completely, decrement by 1
        else if (numInitialCheckInPoints > 0 && numCheckInPoints === 0) {
          checkInDifference = -1;
        }

        if (checkInDifference !== 0) {
          // Update the count for this season
          const currentCount = Number(totalCheckIn[season] || 0);
          const newCount = Math.max(0, currentCount + checkInDifference); // Ensure count doesn't go below 0

          await updateDoc(locationDocRef, {
            [`totalCheckIn.${season}`]: newCount,
          });
        }
      } else if (numCheckInPoints > 0) {
        // Create new location document with initial totalCheckIn object
        await setDoc(
          locationDocRef,
          {
            totalCheckIn: {
              [season]: 1,
            },
          },
          { merge: true }
        ); // Use merge to avoid overwriting other fields
      }
    }
  } catch (error) {
    logger.error("Failed to update user check-in points", error);
  }
};

export const updateUserVolunteerPoints = async (
  groupId,
  clubId,
  memberId,
  eventId,
  season,
  memberName,
  selectedLocationDetails,
  initialVolunteerPoints,
  updatedVolunteerPoints
) => {
  try {
    // Get a reference to the RSVP document
    const rsvpDocRef = doc(
      db,
      "groupAdminPost",
      groupId,
      "posts",
      eventId,
      "rsvp",
      memberId
    );

    // Get the current RSVP document
    const rsvpDocSnap = await getDoc(rsvpDocRef);

    // Initialize objects if document doesn't exist
    let volunteerDetails = {};
    let volunteered = {};

    if (rsvpDocSnap.exists()) {
      const rsvpData = rsvpDocSnap.data();
      volunteerDetails = rsvpData.volunteerDetails || {};
      volunteered = rsvpData.volunteered || {};
    } else {
      // Create the RSVP document if it doesn't exist
      await setDoc(rsvpDocRef, {
        volunteerDetails: {},
        volunteered: {},
        locationIndex: selectedLocationDetails?.locationIndex,
        name: memberName,
        rsvp: {
          status: "attending",
        },
      });
    }

    // Calculate the total points before and after the update
    // For initialVolunteerPoints, sum up all the values
    let initialPointsTotal = 0;
    Object.entries(initialVolunteerPoints || {}).forEach(([, pointValue]) => {
      initialPointsTotal += Number(pointValue || 0);
    });

    // For updatedVolunteerPoints, sum up all the points values from the objects
    let updatedPointsTotal = 0;
    Object.entries(updatedVolunteerPoints || {}).forEach(([, volData]) => {
      if (volData && volData.points !== undefined) {
        updatedPointsTotal += Number(volData.points || 0);
      }
    });

    // Calculate the net change in points
    const pointsDifference = updatedPointsTotal - initialPointsTotal;

    // Process each volunteer entry from updated points
    for (const [volunteerId, volunteer] of Object.entries(
      updatedVolunteerPoints || {}
    )) {
      // Ensure points are treated as numbers
      const pointsAsNumber = Number(volunteer.points || 0);

      if (pointsAsNumber === 0) {
        // Case 1: Points are 0, remove from volunteerDetails and set volunteered to false
        if (volunteerDetails[volunteerId]) {
          delete volunteerDetails[volunteerId];
        }
        volunteered[volunteerId] = false;
      } else {
        // Case 2: Points are more than 0, add to volunteerDetails and set volunteered to true
        volunteerDetails[volunteerId] = {
          name: volunteer.title,
          points: pointsAsNumber, // Store as number
        };
        volunteered[volunteerId] = true;
      }
    }

    // Update the RSVP document
    await updateDoc(rsvpDocRef, {
      volunteerDetails,
      volunteered,
    });

    // Only update the leaderboard if there's a change in points
    if (pointsDifference !== 0) {
      // Update the leaderBoard collection for this member
      const yearDocRef = doc(
        db,
        "leaderBoard",
        clubId,
        "groups",
        groupId,
        "eventLeaderBoard",
        season
      );

      // Get current leaderboard data
      const yearDocSnap = await getDoc(yearDocRef);

      if (yearDocSnap.exists()) {
        const yearData = yearDocSnap.data();
        const memberData = yearData[memberId] || {};
        const currentTotal = Number(memberData.totalVolunteerPoints || 0);

        // Add the difference to the current total
        const newTotal = currentTotal + pointsDifference;

        // Create an update object with the nested field
        const updateObj = {};
        updateObj[`${memberId}.totalVolunteerPoints`] = newTotal;

        await updateDoc(yearDocRef, updateObj);
      } else if (updatedPointsTotal > 0) {
        // Create new entry if it doesn't exist and we have points to add
        const newData = {};
        newData[memberId] = {
          totalVolunteerPoints: updatedPointsTotal,
        };

        await setDoc(yearDocRef, newData);
      }

      // Update the user's event summary in the users subcollection
      const userDocRef = doc(
        db,
        "leaderBoard",
        clubId,
        "groups",
        groupId,
        "eventLeaderBoard",
        season,
        "users",
        memberId
      );

      // Get the user document from the users subcollection
      const userDocSnap = await getDoc(userDocRef);

      if (userDocSnap.exists()) {
        const userData = userDocSnap.data();
        const eventSummary = userData.eventSummary || {};

        // Update the volunteer points for this specific event
        // Set it to the full updated total for this event
        eventSummary[eventId] = {
          ...(eventSummary[eventId] || {}),
          volunteerPoints: updatedPointsTotal,
        };

        // Update the eventSummary in the user document
        await updateDoc(userDocRef, { eventSummary });
      } else if (updatedPointsTotal > 0) {
        // Create new user entry with the event summary
        const newEventSummary = {
          [eventId]: {
            volunteerPoints: updatedPointsTotal,
          },
        };

        // Create the user document in the users subcollection
        await setDoc(userDocRef, { eventSummary: newEventSummary });
      }
    }
  } catch (error) {
    logger.error("Failed to update user volunteer points", error);
  }
};
